import { useCurrentAccount, useWalletConnection } from "@mysten/dapp-kit-react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { truncate } from "truncate-ethereum-address";

import { useClaimsContext } from "@/context/claims/useClaimsContext";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useAccounts, useDisconnectWallet, useSwitchAccount } from "@/hooks/dappkit";
import currency from "@/lib/currency";
import { Button } from "@/ui/nextui/Button/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/shadcn/select";

import { Logo } from "./icons";
import { CurrentAccountCoinObjects } from "./sui/CurrentAccountCoinObjects/CurrentAccountCoinObjects";
import { ConnectWalletButton } from "./wallet/ConnectWalletButton";

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    currentAccountCoins,
    refetchKnownQueries,
    truthCoinBalance,
    selectedDisplayCoinTicker,
    setSelectedDisplayCoinTicker,
  } = useSuiQueryContext();

  // v2: connection flags come from `useWalletConnection()`.
  const { isConnected } = useWalletConnection();
  const { mutate: disconnect } = useDisconnectWallet();
  const { mutate: switchAccount } = useSwitchAccount();
  const accounts = useAccounts();
  const currentAccount = useCurrentAccount();

  const { calculateUnlockedRewards, calculateUnlockedRewardsResponse } = useClaimsContext();

  const hasClaimingEnabled = !!calculateUnlockedRewardsResponse?.isClaimingEnabled;

  // Ensure claim info is loaded when wallet is connected
  useEffect(() => {
    if (currentAccount?.address && !calculateUnlockedRewardsResponse) {
      calculateUnlockedRewards({ user_address: currentAccount.address });
    }
  }, [currentAccount?.address, calculateUnlockedRewardsResponse]);

  // Default to TRUTH once claiming becomes enabled (one-time)
  const hasSetTruthDefaultRef = useRef(false);

  useEffect(() => {
    if (hasClaimingEnabled && !hasSetTruthDefaultRef.current) {
      setSelectedDisplayCoinTicker("TRUTH");
      hasSetTruthDefaultRef.current = true;
    }
  }, [hasClaimingEnabled, setSelectedDisplayCoinTicker]);

  function onSwitchAccount(address: string) {
    const account = accounts.filter((acc) => acc.address === address)[0];

    if (!account) return;

    switchAccount(
      { account },
      {
        onSuccess: () => {
          refetchKnownQueries();
        },
      },
    );
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navigationLinks = [
    { href: "https://cli.swarmnetwork.ai/", label: "Whitepaper" },
    { href: "https://swarmnetwork.ai/blog", label: "Publications" },
    { href: "https://t.me/SwarmNews", label: "Join Community" },
    { href: "https://x.com/getswarmed", label: "Follow on X" },
  ];

  return (
    <>
      <CurrentAccountCoinObjects />

      <header className="fixed top-0 z-50 w-full p-2 sm:p-4">
        {/* Navbar Container */}
        <div className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md">
            {/* Main Content */}
            <div className="relative z-10 px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex items-center justify-between">
                {/* Logo Section */}
                <div className="flex items-center space-x-4 sm:space-x-8">
                  <div className="relative z-20 flex-shrink-0">
                    <Logo className="relative z-10 sm:h-7 sm:w-[120px]" height={24} width={100} />
                  </div>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden items-center space-x-8 xl:flex">
                  {navigationLinks.map((link) => (
                    <a
                      key={link.label}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white no-underline"
                      href={link.href}
                      target="_blank"
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>

                {/* Wallet Section */}
                <div className="hidden items-center space-x-4 md:flex">
                  {/* Wallet Controls - All Connected */}
                  <div className="flex items-center">
                    {!isConnected ? (
                      <ConnectWalletButton className="!h-8 !px-3 !text-xs lg:!h-9 lg:!px-6 lg:!text-sm" />
                    ) : (
                      <div className="flex items-center">
                        {/* Token Balance - Left */}
                        {currentAccountCoins.totalCoinBalance?.text && (
                          <>
                            {!hasClaimingEnabled ? (
                              <div className="hidden h-9 items-center rounded-l-lg border border-r-0 border-white/30 bg-black/20 px-3 lg:flex">
                                <span className="font-mono text-sm font-bold text-white">
                                  {`${currency.convert.toFormattedDigits(
                                    currentAccountCoins.totalCoinBalance.text,
                                  )} USDC`}
                                </span>
                              </div>
                            ) : (
                              <div className="hidden lg:flex">
                                <Select
                                  value={selectedDisplayCoinTicker}
                                  onValueChange={(v) =>
                                    setSelectedDisplayCoinTicker(v as "USDC" | "TRUTH")
                                  }
                                >
                                  <SelectTrigger className="h-9 rounded-l-lg rounded-r-none border border-r-0 border-white/30 bg-black/20 px-3 text-white">
                                    <SelectValue>
                                      <span className="font-mono text-sm font-bold text-white">
                                        {selectedDisplayCoinTicker === "TRUTH"
                                          ? truthCoinBalance?.formatted || "0.00 TRUTH"
                                          : `${currency.convert.toFormattedDigits(
                                              currentAccountCoins.totalCoinBalance.text,
                                            )} USDC`}
                                      </span>
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent className="rounded-lg rounded-t-none border-t-0 border-white/30 bg-black/90 data-[side=bottom]:translate-y-0 data-[side=top]:translate-y-0 [&>div]:p-0">
                                    <div className="p-1">
                                      <SelectItem
                                        className="font-bold text-white focus:bg-white/10"
                                        value="USDC"
                                      >
                                        {`${currency.convert.toFormattedDigits(
                                          currentAccountCoins.totalCoinBalance.text,
                                        )} USDC`}
                                      </SelectItem>
                                      <SelectItem
                                        className="font-bold text-white focus:bg-white/10"
                                        value="TRUTH"
                                      >
                                        {truthCoinBalance?.formatted || "0.00 TRUTH"}
                                      </SelectItem>
                                    </div>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div className="hidden h-8 w-px bg-white/30 lg:block lg:h-9" />
                          </>
                        )}

                        {/* Account Dropdown - Middle */}
                        {accounts.length > 0 && (
                          <div className="relative">
                            <Select
                              defaultValue={accounts[0].address}
                              onValueChange={onSwitchAccount}
                            >
                              <SelectTrigger
                                className={clsx(
                                  "h-8 w-32 border-r-0 border-white/30 bg-black/20 text-xs text-white lg:h-9 lg:w-40 lg:text-sm",
                                  currentAccountCoins.totalCoinBalance?.formatted
                                    ? "rounded-none border-l-0"
                                    : "rounded-l-lg",
                                )}
                              >
                                <SelectValue>{truncate(currentAccount?.address || "")}</SelectValue>
                              </SelectTrigger>
                              <SelectContent className="rounded-lg rounded-t-none border-t-0 border-white/30 bg-black/90 data-[side=bottom]:translate-y-0 data-[side=top]:translate-y-0 [&>div]:p-0">
                                <div className="p-1">
                                  {accounts.map((account, i) => (
                                    <SelectItem
                                      key={`wallet-${i}`}
                                      className="text-white focus:bg-white/10"
                                      value={account.address}
                                    >
                                      {truncate(account.address || "")}
                                    </SelectItem>
                                  ))}
                                </div>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Separator */}
                        <div className="h-8 w-px bg-white/30 lg:h-9" />

                        {/* Disconnect Button - Right */}
                        <Button
                          className="h-8 rounded-l-none rounded-r-lg border border-l-0 border-white/30 bg-black/20 px-3 text-xs text-white lg:h-9 lg:px-4 lg:text-sm"
                          size="sm"
                          onClick={() => disconnect()}
                        >
                          Disconnect
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile menu button */}
                <button
                  aria-label="Toggle menu"
                  className="rounded-lg p-2 text-white md:hidden"
                  onClick={toggleMobileMenu}
                >
                  <div className="flex h-5 w-6 flex-col justify-between">
                    <span
                      className={clsx(
                        "block h-0.5 w-full rounded-full bg-current",
                        isMobileMenuOpen ? "translate-y-2 rotate-45" : "",
                      )}
                      style={{
                        transition: "transform 0.2s ease-out",
                        transformOrigin: "center",
                      }}
                    />
                    <span
                      className={clsx(
                        "block h-0.5 w-full rounded-full bg-current",
                        isMobileMenuOpen ? "opacity-0" : "opacity-100",
                      )}
                      style={{
                        transition: "opacity 0.1s ease-out",
                      }}
                    />
                    <span
                      className={clsx(
                        "block h-0.5 w-full rounded-full bg-current",
                        isMobileMenuOpen ? "-translate-y-2 -rotate-45" : "",
                      )}
                      style={{
                        transition: "transform 0.2s ease-out",
                        transformOrigin: "center",
                      }}
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={clsx(
              "mt-2 origin-top md:hidden",
              isMobileMenuOpen ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0",
            )}
            style={{
              transition: "opacity 0.2s ease-out, transform 0.2s ease-out",
            }}
          >
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md">
              <div className="space-y-6 p-4">
                {/* Mobile Wallet Section */}
                <div className="space-y-4">
                  {!isConnected ? (
                    <div
                      className={clsx(isMobileMenuOpen ? "opacity-100" : "opacity-0")}
                      style={{
                        transition: "opacity 0.3s ease-out",
                        transitionDelay: isMobileMenuOpen ? "0.1s" : "0s",
                      }}
                    >
                      <ConnectWalletButton className="!w-full !py-3 !text-base !font-medium" />
                    </div>
                  ) : (
                    <>
                      {accounts.length > 0 && (
                        <div
                          className={clsx(
                            "space-y-3",
                            isMobileMenuOpen ? "opacity-100" : "opacity-0",
                          )}
                          style={{
                            transition: "opacity 0.3s ease-out",
                            transitionDelay: isMobileMenuOpen ? "0.1s" : "0s",
                          }}
                        >
                          <div className="text-xs font-medium uppercase tracking-wider text-white/60">
                            Account
                          </div>
                          <Select
                            defaultValue={accounts[0].address}
                            onValueChange={onSwitchAccount}
                          >
                            <SelectTrigger className="w-full rounded-lg border-white/30 bg-black/20 py-3 text-white">
                              <SelectValue>{truncate(currentAccount?.address || "")}</SelectValue>
                            </SelectTrigger>
                            <SelectContent className="rounded-lg border-white/30 bg-black/90">
                              {accounts.map((account, i) => (
                                <SelectItem
                                  key={`wallet-${i}`}
                                  className="text-white focus:bg-white/10"
                                  value={account.address}
                                >
                                  {truncate(account.address || "")}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {currentAccountCoins.totalCoinBalance?.formatted && (
                        <div
                          className={clsx(
                            "space-y-2",
                            isMobileMenuOpen ? "opacity-100" : "opacity-0",
                          )}
                          style={{
                            transition: "opacity 0.3s ease-out",
                            transitionDelay: isMobileMenuOpen ? "0.15s" : "0s",
                          }}
                        >
                          <div className="text-xs font-medium uppercase tracking-wider text-white/60">
                            Balance
                          </div>
                          {!hasClaimingEnabled ? (
                            <div className="rounded-lg border border-white/30 bg-black/20 px-4 py-3">
                              <span className="font-mono text-sm font-bold text-white">
                                {`${currency.convert.toFormattedDigits(
                                  currentAccountCoins.totalCoinBalance?.text || "0",
                                )} USDC`}
                              </span>
                            </div>
                          ) : (
                            <Select
                              value={selectedDisplayCoinTicker}
                              onValueChange={(v) =>
                                setSelectedDisplayCoinTicker(v as "USDC" | "TRUTH")
                              }
                            >
                              <SelectTrigger className="w-full rounded-lg border-white/30 bg-black/20 py-3 text-white">
                                <SelectValue>
                                  <span className="font-mono text-sm font-bold text-white">
                                    {selectedDisplayCoinTicker === "TRUTH"
                                      ? truthCoinBalance?.formatted || "0.00 TRUTH"
                                      : `${currency.convert.toFormattedDigits(
                                          currentAccountCoins.totalCoinBalance?.text || "0",
                                        )} USDC`}
                                  </span>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="rounded-lg border-white/30 bg-black/90">
                                <SelectItem
                                  className="font-bold text-white focus:bg-white/10"
                                  value="USDC"
                                >
                                  {`${currency.convert.toFormattedDigits(
                                    currentAccountCoins.totalCoinBalance?.text || "0",
                                  )} USDC`}
                                </SelectItem>
                                <SelectItem
                                  className="font-bold text-white focus:bg-white/10"
                                  value="TRUTH"
                                >
                                  {truthCoinBalance?.formatted || "0.00 TRUTH"}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      )}

                      <div
                        className={clsx(isMobileMenuOpen ? "opacity-100" : "opacity-0")}
                        style={{
                          transition: "opacity 0.3s ease-out",
                          transitionDelay: isMobileMenuOpen ? "0.2s" : "0s",
                        }}
                      >
                        <Button
                          className="w-full rounded-lg border-white/30 bg-black/20 py-3 text-base font-medium text-white"
                          onClick={() => {
                            disconnect();
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Disconnect
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Mobile Navigation */}
                <div className="border-t border-white/20 pt-4">
                  <nav className="grid grid-cols-2 gap-2">
                    {navigationLinks.map((link, index) => (
                      <div
                        key={link.label}
                        className={clsx(isMobileMenuOpen ? "opacity-100" : "opacity-0")}
                        style={{
                          transition: "opacity 0.3s ease-out",
                          transitionDelay: isMobileMenuOpen ? `${0.25 + index * 0.05}s` : "0s",
                        }}
                      >
                        <a
                          className="block rounded-lg px-4 py-3 text-center text-sm font-medium text-white no-underline"
                          href={link.href}
                          target="_blank"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {link.label}
                        </a>
                      </div>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
